import { Helmet } from "react-helmet";
import AnimalGrid from "components/animalgrid";
import BodyText from "components/bodytext";
import ContentBox from "components/contentbox";
import HeaderText from "components/headertext";
import PageGallery from "components/pagegallery";
import Image from "components/image";
import { Link } from "react-router-dom";
import husbandryImg from "img/brbs/Brazilian Rainbow Boas.png";

// Bulk image album
import archiveBrb2 from "img/brbs/BRB004a.jpg";
import archiveBrb4 from "img/brbs/20181229-scan405-3.jpg";
import collectionMeta from "content/collections/brazilian-rainbow-boas.json";
import { animalGridFromJson } from "hooks/useGallery";
import { Body } from "node-fetch";

const animalGridData = animalGridFromJson(collectionMeta);

const BrazilianRainbowBoa = () => {
  return (
    <ContentBox>
      <Helmet>
        <title>{collectionMeta.commonName}</title>
        <meta
          name="description"
          content="See the Brazilian Rainbow Boas (Epicrates cenchria)
            that are being kept here and learn more about their captive husbandry."
        />
        <link rel="canonical" href="https://www.gcreptiles.com/brazilian-rainbow-boas" />
      </Helmet>

      <HeaderText
        title={collectionMeta.commonName}
        subtitle={collectionMeta.scientificName}
      />

      <AnimalGrid data={animalGridData} />

      <BodyText>
        I got my first Brazilian Rainbow boa in the mid-90s as a baby from an ad
        someone had posted in the back of Reptiles magazine. I wish I could
        remember who the person was. The little female snake did awesome for us
        and grew up with lots of people handling her and she never showed the
        slightest sign of discontent. Years later when I heard people say that
        BRBs were very bitey I couldn't believe it, but of course I had a small
        sample size of experience.
      </BodyText>
      <Image
        src={archiveBrb4}
        caption="1997, my first Brazilian Rainbow boa"
      />
      <BodyText>
        Sometime in the early 2000's I got a second BRB, a male, from someone
        locally here in Utah, but again, it has been too long ago for me to
        remember who. The male had a lighter orange to it and was equally tame
        and handleable. At the time there was a certain look to lighter colored
        BRBs in the hobby that was referred to as "Lamar's phase" and there was
        some thought that the male was of that type/phase/variety/whatever. I
        never found out for sure.
      </BodyText>
      <Image
        src={archiveBrb2}
        caption="2003, Comparison of both Brazilian Rainbow boas"
      />
      <BodyText>
        I had hopes to breed them but there was a big setback for me when the
        female was free-roaming the room and got stuck trying to get under a
        door. I took the door off the hinges to get her free but she had damaged
        her spine in the process and was permanently disfigured from that. A
        local vet said that surgery was the only option and that the odds of her
        recovering completely were only about 50/50. So I didn't have that done.
        Given her condition I didn't dare try and breed her. Still, she lived
        for many years after that.
      </BodyText>
      <BodyText>
        I lost both BRBs in the early 2010s. Living in Utah it is extremely dry
        and the relative humidity is usually about 28-30%. This is way too dry
        for BRBs so I was misting them frequently. However, I was keeping them
        in glass-front Vision cages and using newspaper as substrate and that
        just wasn't a good setup for maintaining high enough humidity for the
        animals to thrive so both of them died of respiratory problems.
      </BodyText>
      <BodyText>
        I wasn't going to try high-humidity animals again in my Utah home, but as the
        years have gone on and I've spent more time getting better equipment,
        cages, and methods, I decided I was ready to try BRBs again so in early
        2023 I bought a baby female, Feijoada. Almost 25 years after the first one.
      </BodyText>
      <BodyText>
        Since buying the female I have also bought a male that I've named
        Brigadeiro. Feijoada is a light orange and so with Brigadeiro I wanted 
        one that was a bit darker and redder. I'm very happy with both. But I 
        will say that Feijoada has bit me twice recently and obviously I don't 
        appreciate that.
      </BodyText>
      <h1>Husbandry</h1>
      <Image src={husbandryImg} />
      <BodyText>
        See{" "}
        <Link to="/husbandry-charts">how to read the husbandry charts</Link>.
      </BodyText>
      <BodyText>
        This is the chart that I am using for my Brazilian Rainbow boas.
      </BodyText>
      <BodyText>
        <h3>Temperatures, Humidity, and Light</h3>
        <p>
          As you can see in the chart, I'm keeping my boas at about 78F at night for most
          of the year and then warming them up to about 82F during the day. I recall 
          making it much hotter than this when I was keeping my rainbow boas decades ago
          and I wish I could remember how they reacted to the extra heat. My guess is that
          they were staying on the cool end of their cages. I don't think they like getting
          hot. In fact, I never see mine out basking in the heat after they've eaten a meal.
        </p>
        <p>
          Clearly, coming from the rainforests of South America these snakes need extra
          humidity. I've mentioned that I live in a desert, but I have my boas in cages that
          trap humidity well, they have large water bowls placed underneath the heat panels
          in the cages, and they each have a humidity box. The humidity box is a plastic
          Rubbermaid or Sterilite container that has a lid with a round hole cut in it for
          the snake to enter. And the inside of the box is filled with damp sphagnum moss. 
        </p>
        <p>
          My female spends a significant amount of time in her humidity box and so I change
          out the moss in it every month or two. The male, interestingly enough, spends 
          most of his time <i>underneath</i> his box. Since both of them are shedding great
          and their body condition is excellent I think these conditions are meeting their needs
          humidity-wise.
        </p>
        <h3>Feeding</h3>
        <p>
          Feeding Brazilian Rainbow boas now, and in the past, has always been simple. They
          do fine on a diet of frozen-thawed rodents. I did notice that my female seemed to be growing too thick
          when she was still in her sub-adult stage, so I only feed her small rats every other 
          week now even though she's thick enough to easily handle a medium rat or even a large.
          Next year she will be 4 years old and I will switch her to just one feeding every
          3-4 weeks, except for two months at the end of summer.
        </p>
        <h3>Breeding</h3>
        <p>
          I don't have breeding experience yet with this species but my plan for it is outlined
          in the chart. I will probably give them another year or two before I pair them together.
        </p>
      </BodyText>

      <PageGallery slug="brazilian-rainbow-boas" maxWidth="900" />
    </ContentBox>
  );
};

export default BrazilianRainbowBoa;
